/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { computed, action } from '@ember/object';
import moment from 'moment-timezone';
import { Periods, buildMomentInLocalTime } from 'cio/utils/metrics/common';
import { SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP } from 'cio/utils/metrics/segment-membership';
import {
  LAST_7_DAYS_RANGE_PRESET_UTC,
  LAST_30_DAYS_RANGE_PRESET_UTC,
  LAST_12_WEEKS_RANGE_PRESET_UTC,
  LAST_12_MONTHS_RANGE_PRESET,
} from 'cio/utils/workflow-metrics';
import { RangePreset } from 'cio/utils/date-picker';
import { inject as service } from '@ember/service';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';
import SegmentModel from 'cio/models/segment';
import { TimeRange } from 'cio/utils/time';
import SegmentMembershipDatePickerAllTimePreset from './all-time-preset';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';

interface SegmentMembershipDatePickerArgs {
  timeRange: TimeRange | null;
  timePeriod: string | null;
  onRangeSelect: (range: TimeRange, presetValue: string) => void;
  dateRangePresets?: RangePreset[] | null;
  verticalPosition?: string | null;
  view?: string;
  trackEvent?: string;
  segment?: SegmentModel;
  disabled?: boolean;
  track?: boolean;
  maxYearRange?: number;
}

export default class SegmentMembershipDatePicker extends Component<SegmentMembershipDatePickerArgs> {
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;

  minDate: moment.Moment;
  minDateFromMaxYearRange: moment.Moment;
  maxDate: moment.Moment;

  constructor(owner: unknown, args: SegmentMembershipDatePickerArgs) {
    super(owner, args);

    let membershipReleaseMoment = moment.unix(SEGMENT_MEMBERSHIP_RELEASE_TIMESTAMP).utc().startOf('day');
    this.minDate = moment(`${membershipReleaseMoment.format('YYYY-MM-DD')} 00:00:00`);
    this.minDateFromMaxYearRange = moment(`${membershipReleaseMoment.format('YYYY-MM-DD')} 00:00:00`);

    let now = moment.utc();
    this.maxDate = moment(`${now.format('YYYY-MM-DD')} 00:00:00`);

    if (this.args.maxYearRange != null && this.args.maxYearRange > 0) {
      this.minDateFromMaxYearRange = moment(`${this.maxDate.format('YYYY-MM-DD')} 00:00:00`).subtract(
        this.args.maxYearRange,
        'years',
      );
    }
  }

  get disabled() {
    return this.args.disabled ?? false;
  }

  get track() {
    return this.args.track ?? true;
  }

  get trackEvent() {
    return this.args.trackEvent ?? 'segment_membership_timeframe_filter_changed';
  }

  get verticalPosition() {
    return this.args.verticalPosition ?? 'below';
  }

  get dateRangePresets() {
    if (this.args.dateRangePresets) {
      return this.args.dateRangePresets;
    }

    // Segment memberships shipped on April 9th and is not available for dates
    // before that. This will automatically enable presets as they become
    // available in the future
    let availablePresets = [
      LAST_7_DAYS_RANGE_PRESET_UTC,
      LAST_30_DAYS_RANGE_PRESET_UTC,
      LAST_12_WEEKS_RANGE_PRESET_UTC,
      LAST_12_MONTHS_RANGE_PRESET,
    ].filter((preset) => moment(preset.getRange().start)?.utc().isAfter(this.minDate));

    let start = moment(this.minDate);
    if (this.args.maxYearRange != null && this.args.maxYearRange > 0) {
      start = moment(this.minDateFromMaxYearRange);
    }

    return [
      {
        label: 'All time',
        value: Periods.ALL_TIME,
        getRange: () => ({
          start,
          end: moment().utc().endOf('day'),
        }),
        component: SegmentMembershipDatePickerAllTimePreset,
      },
      ...availablePresets,
    ];
  }

  @computed('args.timeRange.{start,end}', 'args.timePeriod', 'dateRangePresets.[]')
  get timeRange() {
    if (this.args.timeRange) {
      return this.args.timeRange;
    }

    let preset = this.dateRangePresets.find((preset) => preset.value === this.args.timePeriod);

    if (!preset?.getRange) {
      return null;
    }

    return preset.getRange();
  }

  get timeRangeInLocalTimezone() {
    if (!this.timeRange?.start || !this.timeRange?.end) {
      return null;
    }

    return {
      start: buildMomentInLocalTime(this.timeRange.start).startOf('day'),
      end: buildMomentInLocalTime(this.timeRange.end).startOf('day'),
    };
  }

  trackTimeframeSelection(timeRange: TimeRange, timePeriod: string) {
    if (!this.args.view || !this.track) {
      return;
    }

    let data: Record<string, any> = { timeRange, timePeriod };

    if (this.args.view === 'segment' && this.args.segment) {
      data.model = this.args.segment;
    }

    // need to use "any" because otherwise it's hard to satisfy the type
    // requirements while trying to maintain a dynamic event name
    this.analytics.track(this.args.view as any, this.trackEvent as any, data);
  }

  @action
  onRangeSelect(value: any) {
    if (!value.date?.start || !value.date?.end) {
      return;
    }

    let { start, end } = value.date;

    if (this.args.maxYearRange != null && this.args.maxYearRange > 0) {
      let dateDifference = moment(end).diff(moment(start).endOf('day'), 'years', true);

      if (dateDifference > this.args.maxYearRange) {
        this.alerts.warning(
          'segment_membership_date_picker.warning',
          `You cannot filter beyond a maximum of ${formatNumberAndPluralize([this.args.maxYearRange, 'year'])}`,
        );

        end = buildMomentInLocalTime(moment(start).add(this.args.maxYearRange, 'years'));
      }
    }

    let selectedPreset = this.dateRangePresets.find((preset) => {
      let presetRange = preset.getRange();

      if (!presetRange.start || !presetRange.end) {
        return false;
      }

      return presetRange.start.isSame(start) && presetRange.end.isSame(end);
    });

    if (selectedPreset) {
      let range = selectedPreset.getRange();
      let period = selectedPreset.value ?? Periods.CUSTOM;
      this.trackTimeframeSelection(range, period);

      // The API doesn't actually recognize "week" as a period. We only use it
      // in the UI as a preset, and need it to be not named "custom" for
      // analytics tracking purposes. In reality the preset works just like any
      // other custom preset.
      return this.args.onRangeSelect(range, period === 'week' ? Periods.CUSTOM : period);
    } else {
      let adjustedTimeRange = {
        start: moment.tz(`${start.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
        end: moment.tz(`${end.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC'),
      };
      this.trackTimeframeSelection(adjustedTimeRange, Periods.CUSTOM);

      return this.args.onRangeSelect(adjustedTimeRange, Periods.CUSTOM);
    }
  }
}
